/**
 * WebSocket Context Provider
 * Provides WebSocket connection and subscriptions to all components
 */

import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useWebSocket } from '../hooks/useWebSocket';
import type {
  WebSocketContextValue,
  SubscriptionCallback,
  WebSocketManagerOptions,
} from '../types/websocket';

const WebSocketContext = createContext<WebSocketContextValue | undefined>(
  undefined
);

export interface WebSocketProviderProps {
  url: string;
  token?: string;
  autoConnect?: boolean;
  children: React.ReactNode;
  options?: WebSocketManagerOptions;
}

/**
 * WebSocket Provider Component
 */
export function WebSocketProvider({
  url,
  token,
  autoConnect = true,
  children,
  options,
}: WebSocketProviderProps) {
  const {
    isConnected,
    status,
    error,
    subscriptions,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    send,
    updateQueryCache,
  } = useWebSocket(url, {
    token,
    autoConnect: false, // Don't auto-connect in hook, we'll handle it here
    ...options,
  });

  const [internalError, setInternalError] = useState<Error | null>(error);

  // Update internal error state
  useEffect(() => {
    setInternalError(error);
  }, [error]);

  // Connect when token becomes available (only if autoConnect is true)
  // Only call connect() when token first becomes available — react-use-websocket
  // handles reconnect internally via shouldReconnect, so we must NOT call
  // connect() on every disconnected state or it creates a reconnect storm.
  const hasConnectedRef = React.useRef(false);
  useEffect(() => {
    if (autoConnect && token && !hasConnectedRef.current) {
      hasConnectedRef.current = true;
      connect(token).catch(err => {
        console.error('WebSocket auto-connect failed:', err);
      });
    }
  }, [autoConnect, token, connect]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<WebSocketContextValue>(
    () => ({
      isConnected,
      status,
      error: internalError,
      subscriptions,
      connect,
      disconnect,
      subscribe,
      unsubscribe,
      send,
      updateQueryCache,
    }),
    [
      isConnected,
      status,
      internalError,
      subscriptions,
      connect,
      disconnect,
      subscribe,
      unsubscribe,
      send,
      updateQueryCache,
    ]
  );

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Hook to use WebSocket context
 */
export function useWebSocketContext<T = unknown>(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error(
      'useWebSocketContext must be used within WebSocketProvider'
    );
  }
  return context;
}

/**
 * Hook to subscribe to WebSocket events
 * Returns null if WebSocketProvider is not available
 */
export function useWebSocketSubscription<T = unknown>(
  topic: string,
  callback: SubscriptionCallback<T>,
  enabled: boolean = true
): string | null {
  const context = useContext(WebSocketContext);
  const subscriptionIdRef = React.useRef<string | null>(null);
  const callbackRef = React.useRef(callback);

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Undefined when no provider is mounted; the effect below then does nothing
  const subscribe = context?.subscribe;
  const unsubscribe = context?.unsubscribe;

  useEffect(() => {
    if (!subscribe || !unsubscribe) {
      return;
    }

    if (!enabled) {
      if (subscriptionIdRef.current) {
        unsubscribe(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
      return;
    }

    // Use a stable handler that calls the latest callback via ref
    // This prevents re-subscription loops when callback is unstable
    const handler: SubscriptionCallback<T> = data => {
      if (callbackRef.current) {
        callbackRef.current(data);
      }
    };

    subscriptionIdRef.current = subscribe(topic, handler);

    return () => {
      if (subscriptionIdRef.current) {
        unsubscribe(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
    };
  }, [topic, enabled, subscribe, unsubscribe]);

  // If context is not available, return null (WebSocket not available)
  if (!context) {
    return null;
  }

  return subscriptionIdRef.current;
}

/**
 * Hook to send WebSocket messages
 */
export function useWebSocketSend() {
  const { send, isConnected } = useWebSocketContext();

  const sendMessage = useCallback(
    (message: unknown) => {
      if (!isConnected) {
        throw new Error('WebSocket is not connected');
      }
      send(JSON.stringify(message));
    },
    [send, isConnected]
  );

  return sendMessage;
}

/**
 * Hook to handle WebSocket connection status
 */
export function useWebSocketStatus() {
  const { isConnected, status, error } = useWebSocketContext();

  return {
    isConnected,
    status,
    error,
    isConnecting: status === 'connecting',
    isReconnecting: status === 'reconnecting',
    isDisconnected: status === 'disconnected',
  };
}
